import type { HistoryLabelOption, HistoryPrediction } from '../types/history'

const normalizeKey = (value: unknown): string =>
    typeof value === 'string' ? value.trim().toLowerCase() : ''

const resolveUniqueOption = (
    options: HistoryLabelOption[],
    predicate: (option: HistoryLabelOption) => boolean
): HistoryLabelOption | null => {
    const matches = options.filter(predicate)
    return matches.length === 1 ? matches[0] : null
}

const normalizeLanguage = (language?: string): string => {
    const normalized = language?.trim().toLowerCase() || 'zh-cn'
    if (normalized.startsWith('zh-hk') || normalized.startsWith('zh-tw')) {
        return 'zh-HK'
    }
    if (normalized.startsWith('en')) {
        return 'en'
    }
    return 'zh-CN'
}

const SUMMARY_SEGMENT_PATTERN = /^(.+?)(\((\d+)\))$/

const formatDisplayNameWithCode = (code: string, displayName: string): string => {
    const normalizedCode = code.trim()
    const normalizedDisplayName = displayName.trim()
    if (!normalizedCode) {
        return normalizedDisplayName
    }
    if (!normalizedDisplayName || normalizedDisplayName === normalizedCode) {
        return normalizedCode
    }
    return `${normalizedCode} · ${normalizedDisplayName}`
}

export const getManualRecognitionTypeDisplayName = (
    option: HistoryLabelOption,
    language?: string
): string => {
    const localizedName = option.display_names?.[normalizeLanguage(language)]
    return localizedName?.trim() || option.display_name || option.class
}

export const formatManualRecognitionSummary = (
    summary: string | null | undefined,
    options: HistoryLabelOption[] | null | undefined,
    language?: string
): string | null => {
    if (!summary) {
        return summary ?? null
    }

    const optionByKey = new Map<string, HistoryLabelOption>()
    for (const option of options ?? []) {
        const manualTypeKey = normalizeKey(option.manual_type_key)
        if (manualTypeKey && !optionByKey.has(manualTypeKey)) {
            optionByKey.set(manualTypeKey, option)
        }
    }

    const counts = new Map<string, number>()
    const orderedKeys: string[] = []
    let parsedSegmentCount = 0
    for (const part of summary.split(',')) {
        const segment = part.trim()
        if (!segment) {
            continue
        }

        const match = segment.match(SUMMARY_SEGMENT_PATTERN)
        const rawLabel = (match?.[1] ?? segment).trim()
        if (!rawLabel) {
            continue
        }

        const manualTypeKey = rawLabel.split('·')[0].trim() || rawLabel
        if (!counts.has(manualTypeKey)) {
            orderedKeys.push(manualTypeKey)
            counts.set(manualTypeKey, 0)
        }
        const count = Number(match?.[3] ?? '1')
        counts.set(
            manualTypeKey,
            (counts.get(manualTypeKey) ?? 0) +
                (Number.isFinite(count) ? count : 1)
        )
        parsedSegmentCount += 1
    }

    if (parsedSegmentCount === 0) {
        return null
    }

    return orderedKeys
        .map((manualTypeKey) => {
            const option = optionByKey.get(normalizeKey(manualTypeKey))
            const displayName = option
                ? getManualRecognitionTypeDisplayName(option, language)
                : manualTypeKey
            const label = formatDisplayNameWithCode(
                manualTypeKey,
                displayName
            )
            return `${label}(${counts.get(manualTypeKey) ?? 0})`
        })
        .join(', ')
}

export const resolveManualRecognitionTypeOption = (
    prediction: HistoryPrediction | null | undefined,
    options: HistoryLabelOption[]
): HistoryLabelOption | null => {
    if (!prediction) {
        return null
    }

    const manualTypeKey = normalizeKey(prediction.manual_type_key)
    if (manualTypeKey) {
        const manualTypeMatch = resolveUniqueOption(
            options,
            (option) => normalizeKey(option.manual_type_key) === manualTypeKey
        )
        if (manualTypeMatch) {
            return manualTypeMatch
        }
    }

    const rawClassNorm = normalizeKey(
        prediction.raw_class_norm || prediction.raw_class
    )
    if (rawClassNorm) {
        const directBindingMatch = resolveUniqueOption(options, (option) =>
            (option.model_type_bindings ?? []).some(
                (binding) =>
                    normalizeKey(binding.raw_class_norm) === rawClassNorm
            )
        )
        if (directBindingMatch) {
            return directBindingMatch
        }
    }

    return null
}

export const projectPredictionsToManualRecognitionTypes = (
    predictions: HistoryPrediction[] | null | undefined,
    options: HistoryLabelOption[] | null | undefined,
    language?: string
): HistoryPrediction[] => {
    if (!Array.isArray(predictions) || !Array.isArray(options)) {
        return []
    }

    return predictions.flatMap((prediction) => {
        const option = resolveManualRecognitionTypeOption(prediction, options)
        if (!option?.manual_type_key) {
            return []
        }
        const sourcePrediction = { ...prediction }
        Reflect.deleteProperty(sourcePrediction, 'display_category_key')

        return [
            {
                ...sourcePrediction,
                class: getManualRecognitionTypeDisplayName(option, language),
                eng_class: option.eng_class || option.manual_type_key,
                manual_type_key: option.manual_type_key,
                cls_id: option.cls_id,
            },
        ]
    })
}
